/**
 * Enum defining the available repayment plan types.
 * Use these values when selecting or referencing a plan.
 */
export enum PlanType {
  SAVE = 'SAVE',
  PAYE = 'PAYE', // Pay As You Earn
  NEW_IBR = 'NEW_IBR', // Income-Based Repayment (New Borrower, 10%/20yr)
  ICR = 'ICR',
  OLD_IBR = 'OLD_IBR',
  RAP = 'RAP', // Repayment Assistance Plan
  PRIVATE_REFINANCE = 'PRIVATE_REFINANCE',
  STANDARD_10_YEAR = 'STANDARD_10_YEAR',
  STANDARD_REPAYMENT = 'STANDARD_REPAYMENT', // Standard Repayment Plan with variable term
  EXTENDED_25_YEAR = 'EXTENDED_25_YEAR',
  SAVE_PSLF = 'SAVE_PSLF',
  PAYE_PSLF = 'PAYE_PSLF',
  NEW_IBR_PSLF = 'NEW_IBR_PSLF',
  ICR_PSLF = 'ICR_PSLF',
  OLD_IBR_PSLF = 'OLD_IBR_PSLF',
  RAP_PSLF = 'RAP_PSLF',
  GRADUATED = 'GRADUATED',
  EXTENDED_GRADUATED = 'EXTENDED_GRADUATED',
  // Note: Old IBR is generally not eligible for PSLF, but edge cases might exist. Omitting OLD_IBR_PSLF for simplicity.
}

export enum DisplayPlanGroup {
  SAVE = 'SAVE',
  PAYE = 'PAYE',
  NEW_IBR = 'NEW_IBR',
  OLD_IBR = 'OLD_IBR',
  ICR = 'ICR',
  RAP = 'RAP',
  STANDARD_10 = 'STANDARD_10_YEAR',
  STANDARD_REPAYMENT = 'STANDARD_REPAYMENT',
  EXTENDED_25 = 'EXTENDED_25_YEAR',
  GRADUATED_10 = 'GRADUATED_10_YEAR',
  EXTENDED_GRADUATED_25 = 'EXTENDED_GRADUATED_25_YEAR',
  PRIVATE_REFINANCE = 'PRIVATE_REFINANCE',
}

export const CANONICAL_DISPLAY_ORDER: DisplayPlanGroup[] = [
  DisplayPlanGroup.RAP,
  DisplayPlanGroup.STANDARD_REPAYMENT,
  DisplayPlanGroup.SAVE,
  DisplayPlanGroup.PAYE,
  DisplayPlanGroup.NEW_IBR,
  DisplayPlanGroup.OLD_IBR,
  DisplayPlanGroup.ICR,
  DisplayPlanGroup.STANDARD_10,
  DisplayPlanGroup.GRADUATED_10,
  DisplayPlanGroup.EXTENDED_25,
  DisplayPlanGroup.EXTENDED_GRADUATED_25,
  DisplayPlanGroup.PRIVATE_REFINANCE,
];

export type BorrowerType = 'user' | 'partner';

export const DISPLAY_GROUP_LABELS: Record<DisplayPlanGroup, string> = {
  [DisplayPlanGroup.SAVE]: 'SAVE',
  [DisplayPlanGroup.PAYE]: 'PAYE',
  [DisplayPlanGroup.NEW_IBR]: 'IBR (new borrower terms)',
  [DisplayPlanGroup.OLD_IBR]: 'IBR (old borrower terms)',
  [DisplayPlanGroup.ICR]: 'ICR',
  [DisplayPlanGroup.RAP]: 'RAP (Repayment Assistance Plan)',
  [DisplayPlanGroup.STANDARD_10]: '10-year fixed',
  [DisplayPlanGroup.STANDARD_REPAYMENT]: 'Standard repayment',
  [DisplayPlanGroup.EXTENDED_25]: '25-year fixed',
  [DisplayPlanGroup.GRADUATED_10]: '10-year graduated',
  [DisplayPlanGroup.EXTENDED_GRADUATED_25]: '25-year graduated',
  [DisplayPlanGroup.PRIVATE_REFINANCE]: 'Private refinancing',
};

// Mapping DisplayPlanGroup to potential underlying PlanTypes, ordered by preference
export const DISPLAY_GROUP_TO_PLAN_TYPES: Record<DisplayPlanGroup, PlanType[]> = {
  [DisplayPlanGroup.SAVE]: [PlanType.SAVE_PSLF, PlanType.SAVE],
  [DisplayPlanGroup.PAYE]: [PlanType.PAYE_PSLF, PlanType.PAYE],
  [DisplayPlanGroup.NEW_IBR]: [PlanType.NEW_IBR_PSLF, PlanType.NEW_IBR],
  [DisplayPlanGroup.OLD_IBR]: [PlanType.OLD_IBR_PSLF, PlanType.OLD_IBR],
  [DisplayPlanGroup.ICR]: [PlanType.ICR_PSLF, PlanType.ICR],
  [DisplayPlanGroup.RAP]: [PlanType.RAP_PSLF, PlanType.RAP],
  [DisplayPlanGroup.STANDARD_10]: [PlanType.STANDARD_10_YEAR],
  [DisplayPlanGroup.STANDARD_REPAYMENT]: [PlanType.STANDARD_REPAYMENT],
  [DisplayPlanGroup.EXTENDED_25]: [PlanType.EXTENDED_25_YEAR],
  [DisplayPlanGroup.GRADUATED_10]: [PlanType.GRADUATED],
  [DisplayPlanGroup.EXTENDED_GRADUATED_25]: [PlanType.EXTENDED_GRADUATED],
  [DisplayPlanGroup.PRIVATE_REFINANCE]: [PlanType.PRIVATE_REFINANCE],
};

/**
 * User-friendly labels for each plan type.
 */
export const PLAN_LABELS: Record<PlanType, string> = {
  [PlanType.SAVE]: 'SAVE',
  [PlanType.PAYE]: 'PAYE',
  [PlanType.NEW_IBR]: 'IBR (New borrower)',
  [PlanType.ICR]: 'ICR',
  [PlanType.OLD_IBR]: 'IBR (Old borrower)',
  [PlanType.RAP]: 'RAP (Repayment Assistance Plan)',
  [PlanType.PRIVATE_REFINANCE]: 'Private refinancing (11 year @ 7%)',
  [PlanType.STANDARD_10_YEAR]: '10-year fixed',
  [PlanType.STANDARD_REPAYMENT]: 'Standard repayment',
  [PlanType.EXTENDED_25_YEAR]: '25-year fixed',
  [PlanType.SAVE_PSLF]: 'SAVE + PSLF',
  [PlanType.PAYE_PSLF]: 'PAYE + PSLF',
  [PlanType.NEW_IBR_PSLF]: 'IBR (New borrower) + PSLF',
  [PlanType.ICR_PSLF]: 'ICR + PSLF',
  [PlanType.OLD_IBR_PSLF]: 'IBR (Old borrower) + PSLF',
  [PlanType.RAP_PSLF]: 'RAP (Repayment Assistance Plan) + PSLF',
  [PlanType.GRADUATED]: '10-year graduated',
  [PlanType.EXTENDED_GRADUATED]: '25-year graduated',
};

/**
 * User-friendly term labels for each plan type.
 */
export const PLAN_TERM_LABELS: Record<PlanType, string> = {
  [PlanType.SAVE]: '25', // Depending on loan type/principal
  [PlanType.PAYE]: '20',
  [PlanType.NEW_IBR]: '20',
  [PlanType.ICR]: '25',
  [PlanType.OLD_IBR]: '25',
  [PlanType.RAP]: '30',
  [PlanType.PRIVATE_REFINANCE]: '11',
  [PlanType.STANDARD_10_YEAR]: '10',
  [PlanType.STANDARD_REPAYMENT]: '10-25', // Variable term based on balance
  [PlanType.EXTENDED_25_YEAR]: '25',
  [PlanType.SAVE_PSLF]: '10',
  [PlanType.PAYE_PSLF]: '10',
  [PlanType.NEW_IBR_PSLF]: '10',
  [PlanType.ICR_PSLF]: '10',
  [PlanType.OLD_IBR_PSLF]: '10',
  [PlanType.RAP_PSLF]: '10',
  [PlanType.GRADUATED]: '10',
  [PlanType.EXTENDED_GRADUATED]: '25',
};

/**
 * Configuration parameters specific to Income-Driven Repayment (IDR) plans.
 */
export interface PlanIdrConfig {
  type: 'IDR';
  // Assumption: All loans are grad loans (10% payment factor) instead of undergrad (5% payment factor)
  paymentFactor: number | null; // Percentage of discretionary income (e.g., 0.1 for 10%), null for special calculation
  povertyLineFactor: number | null; // Multiplier for Federal Poverty Level (e.g., 1.5 for 150%), null for special calculation
  forgivenessYears: number; // Base years until forgiveness (non-PSLF)
  // Boolean indicating if the plan offers full interest subsidy like SAVE.
  // true, interest accrued beyond the interest payment amount does not increase the balance.
  interestSubsidyApplies: boolean;
  isPslfEligible: boolean; // Can this plan be used for PSLF?
  // IDR plans generally do not capitalize interest monthly.
  capitalizationStrategy: 'never';
}

/**
 * Configuration parameters specific to Fixed Payment plans (Standard, Extended, Private).
 */
export interface PlanFixedConfig {
  type: 'FIXED';
  termYears: number; // Loan term in years
  // Fixed plans capitalize interest monthly.
  capitalizationStrategy: 'always';
  // Optional fixed interest rate for private loans. absent, use the loan's original rate.
  fixedInterestRate?: number;
}

/**
 * Configuration parameters specific to Graduated Payment plans.
 */
export interface PlanGraduatedConfig {
  type: 'GRADUATED'; // Marker type for graduated plans
  termYears: number;
  capitalizationStrategy: 'never'; // Interest accrues separately
}

/**
 * Union type representing the configuration for any repayment plan.
 */
export type PlanConfig = PlanIdrConfig | PlanFixedConfig | PlanGraduatedConfig;

// Define the final exported map, including PSLF variants, Graduated plans, and Fixed plans
// Define each plan configuration explicitly without inheritance for clarity
export const PLAN_CONFIGS: Record<PlanType, PlanConfig> = {
  // --- IDR Plans ---
  [PlanType.SAVE]: {
    type: 'IDR',
    paymentFactor: 0.1, // Assumption: All loans are grad loans (10% payment factor) instead of undergrad (5% payment factor)
    povertyLineFactor: 2.25,
    // NOTE: Base forgiveness years set to max placeholder. Adjusted dynamically in planCalculator based on principal/degree.
    forgivenessYears: 25,
    interestSubsidyApplies: true, // SAVE fully subsidizes unpaid accrued interest
    isPslfEligible: true,
    capitalizationStrategy: 'never',
  },
  [PlanType.PAYE]: {
    type: 'IDR',
    paymentFactor: 0.1,
    povertyLineFactor: 1.5,
    forgivenessYears: 20,
    interestSubsidyApplies: false, // Assumption: no interest subsidy
    isPslfEligible: true,
    capitalizationStrategy: 'never',
  },
  [PlanType.NEW_IBR]: {
    type: 'IDR',
    paymentFactor: 0.1,
    povertyLineFactor: 1.5,
    forgivenessYears: 20,
    interestSubsidyApplies: false, // Assumption: no interest subsidy
    isPslfEligible: true,
    capitalizationStrategy: 'never',
  },
  [PlanType.ICR]: {
    type: 'IDR',
    paymentFactor: 0.2, // Using the 20% discretionary income part for now
    povertyLineFactor: 1.0,
    forgivenessYears: 25,
    interestSubsidyApplies: false, // No interest subsidy
    isPslfEligible: true,
    capitalizationStrategy: 'never',
    // Assumption: Uses simplified alternative calc (lesser of 20% disc. income or 12-yr fixed payment on current balance).
  },
  [PlanType.OLD_IBR]: {
    type: 'IDR',
    paymentFactor: 0.15,
    povertyLineFactor: 1.5,
    forgivenessYears: 25,
    interestSubsidyApplies: false,
    isPslfEligible: true, // Old IBR is PSLF eligible
    capitalizationStrategy: 'never',
    // Assumption: Uses simplified alternative calc (lesser of 20% disc. income or 12-yr fixed payment on current balance).
  },

  // --- PSLF Variants (based on underlying IDR plans) ---
  // PSLF overrides forgiveness years to 10, keeps other IDR rules
  [PlanType.SAVE_PSLF]: {
    type: 'IDR',
    paymentFactor: 0.1, // Base factor, adjusted dynamically
    povertyLineFactor: 2.25,
    interestSubsidyApplies: true,
    isPslfEligible: true,
    capitalizationStrategy: 'never',
    forgivenessYears: 10, // Override forgiveness years for PSLF
  },
  [PlanType.PAYE_PSLF]: {
    type: 'IDR',
    paymentFactor: 0.1,
    povertyLineFactor: 1.5,
    interestSubsidyApplies: false,
    isPslfEligible: true,
    capitalizationStrategy: 'never',
    forgivenessYears: 10,
  },
  [PlanType.NEW_IBR_PSLF]: {
    type: 'IDR',
    paymentFactor: 0.1,
    povertyLineFactor: 1.5,
    interestSubsidyApplies: false,
    isPslfEligible: true,
    capitalizationStrategy: 'never',
    forgivenessYears: 10,
  },
  [PlanType.ICR_PSLF]: {
    type: 'IDR',
    paymentFactor: 0.2,
    povertyLineFactor: 1.0,
    interestSubsidyApplies: false,
    isPslfEligible: true,
    capitalizationStrategy: 'never',
    forgivenessYears: 10,
  },
  [PlanType.OLD_IBR_PSLF]: {
    type: 'IDR',
    paymentFactor: 0.15,
    povertyLineFactor: 1.5,
    interestSubsidyApplies: false,
    isPslfEligible: true,
    capitalizationStrategy: 'never',
    forgivenessYears: 10,
  },

  // --- RAP Plans ---
  [PlanType.RAP]: {
    type: 'IDR',
    paymentFactor: null, // Special calculation based on AGI
    povertyLineFactor: null, // Special calculation based on AGI
    forgivenessYears: 30,
    interestSubsidyApplies: true,
    isPslfEligible: true,
    capitalizationStrategy: 'never',
  },
  [PlanType.RAP_PSLF]: {
    type: 'IDR',
    paymentFactor: null, // Special calculation based on AGI
    povertyLineFactor: null, // Special calculation based on AGI,
    forgivenessYears: 10,
    interestSubsidyApplies: true,
    isPslfEligible: true,
    capitalizationStrategy: 'never',
  },

  // --- Fixed Plans ---
  [PlanType.STANDARD_10_YEAR]: {
    type: 'FIXED',
    termYears: 10,
    capitalizationStrategy: 'always',
  },
  [PlanType.STANDARD_REPAYMENT]: {
    type: 'FIXED',
    termYears: -1, // Variable term based on balance (10-25 years), will be calculated dynamically
    capitalizationStrategy: 'always',
  },
  [PlanType.EXTENDED_25_YEAR]: {
    type: 'FIXED',
    termYears: 25,
    capitalizationStrategy: 'always',
  },
  [PlanType.PRIVATE_REFINANCE]: {
    type: 'FIXED',
    termYears: -1, // This will always be overridden by input
    capitalizationStrategy: 'always',
  },

  // --- Graduated Plans ---
  [PlanType.GRADUATED]: {
    type: 'GRADUATED',
    termYears: 10,
    capitalizationStrategy: 'never',
  },
  [PlanType.EXTENDED_GRADUATED]: {
    type: 'GRADUATED', // Shares the same structure
    termYears: 25, // Max term for extended plans
    capitalizationStrategy: 'never',
  },
};

export interface SummaryMetrics {
  firstMonthlyPayment: number | null;
  totalPayment: number | null;
  finishDate: Date | null;
}

/**
 * Maps loan award IDs to their selected plan type.
 * Used to track which plan is selected for each loan.
 */
export type PerLoanPlanSelection = Record<string, PlanType | null>;

/**
 * Plan availability groups based on implementation dates
 */
export interface PlanAvailabilityGroup {
  id: string;
  label: string;
  description: string;
  plans: DisplayPlanGroup[];
}

export const PLAN_AVAILABILITY_GROUPS: PlanAvailabilityGroup[] = [
  {
    id: 'new_2026',
    label: 'Available starting July\u00A02026',
    description: 'New plans from the "One Big Beautiful Bill"',
    plans: [DisplayPlanGroup.RAP, DisplayPlanGroup.STANDARD_REPAYMENT],
  },
  {
    id: 'grandfathered_2026',
    label: 'Available until July\u00A02026',
    description:
      'Existing plans available for new borrowers before July\u00A01,\u00A02026 and can potentially be continued',
    plans: [
      DisplayPlanGroup.NEW_IBR,
      DisplayPlanGroup.OLD_IBR,
      DisplayPlanGroup.STANDARD_10,
      DisplayPlanGroup.EXTENDED_25,
      DisplayPlanGroup.GRADUATED_10,
      DisplayPlanGroup.EXTENDED_GRADUATED_25,
    ],
  },
  {
    id: 'phased_out_2028',
    label: 'Phased out by July\u00A02028',
    description:
      'Plans that will be phased out and replaced with RAP or Standard Repayment by July\u00A01,\u00A02028 or earlier ',
    plans: [DisplayPlanGroup.SAVE, DisplayPlanGroup.PAYE, DisplayPlanGroup.ICR],
  },
  {
    id: 'always_available',
    label: 'Typically available',
    description: 'Generally an option for most borrowers, subject to credit approval',
    plans: [DisplayPlanGroup.PRIVATE_REFINANCE],
  },
];
